import React, { useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import Button from '../../../../../../components/Button'
import FacebookPost from './FacebookPost'
import ErrorMessage from '../../../../../../components/Form/ErrorMessage'
import FacebookPostsListSkeleton from './FacebookPost/FacebookPostsListSkeleton'

import useGetInstagramAccounts from '../../../../../../hooks/instagramAccountsHooks/useGetInstagramAccounts'

import { clearFacebookPosts, getFacebookPosts } from '../../../../../../modules/actions/ads'
import { selectedFacebookAdAccountSelector } from '../../../../../../modules/selectors/adAccounts'
import {
  facebookPostsErrorSelector,
  facebookPostsSelector,
  facebookPostsWasLoadedSelector
} from '../../../../../../modules/selectors/ads'

import { AD_TEXT, EXISTING_POST, FB_PAGE_CHOICE } from '../../../fields'
import { FILE_URL } from '../../../../../ReusableFormFields/AdFileUpload/fields'

import useStyles from '../../../../../../styles/common/stepForms'

const stepFields = [EXISTING_POST]

function ExistingPostStep({ formik, handleStepChange }) {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const classes = useStyles()

  const { id: selectedAdAccountId } = useSelector(selectedFacebookAdAccountSelector)

  const facebookPosts = useSelector(facebookPostsSelector)
  const facebookPostsWasLoaded = useSelector(facebookPostsWasLoadedSelector)
  const facebookPostsError = useSelector(facebookPostsErrorSelector)

  const facebookPostsDidLoading = facebookPostsWasLoaded || facebookPostsError

  const handleContinue = () => {
    handleStepChange(stepFields)
  }

  const { values, errors, touched, setValues, setFieldValue } = formik

  const facebookPageId = values[FB_PAGE_CHOICE]
  const selectedPostId = values[EXISTING_POST]
  const selectedPostError = errors[EXISTING_POST]
  const selectedPostTouched = touched[EXISTING_POST]

  const { selectedPageTokenData } = useGetInstagramAccounts(facebookPageId)

  const { token } = selectedPageTokenData

  const selectPostHandler = useCallback(
    post => {
      setValues({
        ...values,
        [EXISTING_POST]: post.id,
        [AD_TEXT]: post.message,
        [FILE_URL]: post.full_picture
      })
    },
    [setValues, values]
  )

  const renderFacebookPosts = useCallback(() => {
    if (!facebookPostsDidLoading) {
      return <FacebookPostsListSkeleton />
    } else if (facebookPostsWasLoaded && facebookPosts?.length) {
      return facebookPosts.map(post => (
        <FacebookPost
          key={post.id}
          post={post}
          isSelected={selectedPostId === post.id}
          onClick={() => selectPostHandler(post)}
        />
      ))
    } else {
      return <p>{t('There are no posts eligible for promotion')}</p>
    }
  }, [t, facebookPosts, facebookPostsWasLoaded, facebookPostsDidLoading, selectedPostId, selectPostHandler])

  useEffect(() => {
    if (selectedAdAccountId && token) {
      dispatch(
        getFacebookPosts({
          facebookPageId,
          account: selectedAdAccountId,
          page_access_token: token,
          is_eligible_for_promotion: true
        })
      )
    } else {
      // Clear posts if user changed page and token request fails
      dispatch(clearFacebookPosts())
      setFieldValue(EXISTING_POST, '')
    }
  }, [dispatch, token, facebookPageId, selectedAdAccountId, setFieldValue])

  useEffect(
    () => () => {
      dispatch(clearFacebookPosts())
    },
    [dispatch]
  )

  return (
    <>
      <div className={classes.stepTitle}>{t('Which post do you want to promote?')}</div>
      <div className={classes.stepBody}>
        {renderFacebookPosts()}
        {selectedPostError && selectedPostTouched && <ErrorMessage error={selectedPostError} />}
      </div>
      <div className={classes.stepFooter}>
        <Button type="button" className="dark" onClick={handleContinue}>
          {t('Continue')}
        </Button>
      </div>
    </>
  )
}

export default ExistingPostStep
